/**
 * useUserContext — lox247 enriched user state.
 *
 * Wraps GET /api/me, which returns:
 *  - `user`: better-auth user
 *  - `isAdmin`: email matches ADMIN_EMAILS env var
 *  - `isApproved`: customers.approved === true (Gordon approved this buyer)
 *  - `customer`: lazy-created customers row
 *
 * Use this for app-wide gating (admin-only routes, pending-approval redirect,
 * checkout availability). The base `useSession()` hook from better-auth
 * doesn't know about lox247-specific state.
 */
import { useQuery } from '@tanstack/react-query'
import { apiClient } from './api-client'

export interface UserContextResponse {
  user: {
    id: string
    email: string
    name: string
    image?: string | null
    role: 'user' | 'manager' | 'admin'
  }
  isAdmin: boolean
  isApproved: boolean
  customer: {
    id: string
    approved: boolean
    approvedAt: string | null
    approvedBy: string | null
    businessName: string | null
    businessAbn: string | null
    notes: string | null
    createdAt: string
    updatedAt: string
  } | null
}

export const userContextKey = ['user-context'] as const

export function useUserContext(options: { enabled?: boolean } = {}) {
  return useQuery<UserContextResponse>({
    queryKey: userContextKey,
    queryFn: () => apiClient.get<UserContextResponse>('/api/me'),
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
    retry: false,
  })
}
